import React, {useState} from 'react';
import {CenteredSpaceWrap} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import * as S from './ToggleButtonList.styles';
import {defaultTheme} from '@stencil/build-theme';
import useToggle from '../../../../../shared/hooks/useToggle';
import useArrayToggler from '../../../../../shared/hooks/useArrayToggler';
import {ArrayTogglerBehaviorTypes} from '../../../../../shared/hooks/useArrayToggler';
import {isDefined} from './../../../../../types/type-guards';
import FiltersIconSelector from '../../../../../shared/components/FiltersIconSelector';
import HappyDropDownIcon from '@stencil/icons/src/HappyDropDownIcon/HappyDropDownIcon';

interface IToggleButtonList<T extends unknown, K extends keyof T> {
  name: string;
  type: keyof typeof ArrayTogglerBehaviorTypes;
  data: T[];
  lookup: (item: T) => T[K] | T;
  renderItem: (item: T) => JSX.Element;
  actionHandler: (data: T[]) => void;
  resetHandlerProp?: () => void;
  defaultState?: T[];
  isSingleFilter?: boolean;
  maxLength?: number;
  filterDescription?: {};
  contentsWrapperStyles?: {
    [K: string]: string; // 🐨 make it take only css properties
  };
}

function ToggleButtonList<T extends unknown, K extends keyof T>(props: IToggleButtonList<T, K>) {
  const {
    name,
    type,
    data,
    lookup,
    renderItem,
    actionHandler,
    resetHandlerProp,
    defaultState,
    isSingleFilter = false,
    maxLength,
    contentsWrapperStyles = {},
  } = props;
  const [showMore, setShowMore] = useState(false);
  const [toggleValue, toggleHandler] = useToggle();
  const [isChecked, onClickHandler, resetHandler] = useArrayToggler(
    data,
    lookup,
    type,
    actionHandler,
    defaultState,
  );

  const hasAnyItemSelected = data.find(item => isChecked(item));

  return (
    <>
      <S.CheckboxListWrapperDiv isSingleFilter={isSingleFilter}>
        {(!isSingleFilter && (
          <CenteredSpaceWrap>
            <CenteredSpaceWrap
              onClick={() => {
                toggleHandler(!toggleValue);
              }}
            >
              {toggleValue ? (
                <HappyDropDownIcon
                  margin="0 1rem 0 0"
                  height="1rem"
                  width="1rem"
                  fill={defaultTheme.colors.downriver.base}
                />
              ) : (
                <S.ArrowDropDownCollapsedIcon
                  height="1rem"
                  width="1rem"
                  fill={defaultTheme.colors.downriver.base}
                />
              )}
              <S.StyledH5>{name}</S.StyledH5>
            </CenteredSpaceWrap>
            <div
              onClick={() => {
                resetHandler();
                if (isDefined(resetHandlerProp)) resetHandlerProp();
              }}
            >
              <S.ResetTextWrapper isactive={hasAnyItemSelected ? 'true' : 'false'}>
                RESET
              </S.ResetTextWrapper>
            </div>
          </CenteredSpaceWrap>
        )) ||
          null}

        {toggleValue &&
          data.map((item, index) => {
            if (!item) return;
            if (!showMore && maxLength && index > maxLength - 1) return;
            let fillColor =
              item.n === 'Book Now, Pay Later Available'
                ? defaultTheme.colors.goTeal.light
                : defaultTheme.colors.goGreen.light;
            return (
              <div key={name + index}>
                <S.FilterToggleContainerOuterWrap>
                  <S.FilterToggleInnerWrap
                    onClick={event => {
                      event.preventDefault();
                      onClickHandler(item);
                    }}
                  >
                    <S.FilterToggleHeaderWrap fillType={fillColor}>
                      <FiltersIconSelector
                        name={item.n}
                        styleProps={{
                          fill:
                            item.n === 'Book Now, Pay Later Available'
                              ? defaultTheme.colors.goTeal.dark
                              : defaultTheme.colors.goGreen.base,
                          width: '1.5rem',
                          height: '1.5rem',
                        }}
                      />
                      <S.FilterToggleHeaderItem>{renderItem(item)}</S.FilterToggleHeaderItem>
                    </S.FilterToggleHeaderWrap>
                    <S.FilterToggleSwitchWrapper>
                      <S.FilterToggleItemDescription>{item.text}</S.FilterToggleItemDescription>
                      <S.FilterToggleSwitchLabel>
                        <S.FilterToggleSwitchInput
                          type="checkbox"
                          checked={isChecked(item)}
                          {...contentsWrapperStyles}
                        />
                        <S.FilterToggleCircularSwitch />
                      </S.FilterToggleSwitchLabel>
                    </S.FilterToggleSwitchWrapper>
                  </S.FilterToggleInnerWrap>
                </S.FilterToggleContainerOuterWrap>
              </div>
            );
          })}

        {(maxLength && (
          <div>
            <S.ShowAllText onClick={() => setShowMore(!showMore)}>
              {(!showMore && '+') || '-'} Show {(!showMore && 'All') || 'Less'}
            </S.ShowAllText>
          </div>
        )) ||
          null}
      </S.CheckboxListWrapperDiv>
    </>
  );
}

export default React.memo(ToggleButtonList);
